import { IDLE, reduce, type StrokeOps, type StrokeState } from "#canvas/strokeReducer.ts";
import type { Cell } from "#canvas/types.ts";
import { type PointerEvent as ReactPointerEvent, useCallback, useLayoutEffect, useRef } from "react";

interface PointerToolsDeps extends StrokeOps {
  /** Translate a React pointer event to a grid cell (or null if out of bounds). */
  eventToCell: (e: ReactPointerEvent<HTMLDivElement>) => Cell | null;
}

interface PointerToolsHandlers {
  /** Wire to the wrapper div's `onPointerDown`. Decides pan vs. tool stroke and captures the pointer. */
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  /** Wire to `onPointerMove`. No-op when idle; otherwise feeds the reducer. */
  onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  /** Wire to `onPointerUp`. Commits strokes (except SELECT) and releases pointer capture. */
  onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  /** Wire to `onPointerCancel`. Discards in-progress live edits — distinct from up. */
  onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => void;
}

function safeReleaseCapture(e: ReactPointerEvent<HTMLDivElement>): void {
  try {
    e.currentTarget.releasePointerCapture(e.pointerId);
  } catch {
    // capture may already be lost
  }
}

/**
 * Drives the stroke reducer from React pointer events. Owns:
 *   - `stateRef`: current gesture state (idle / panning / freehand / shape / tap)
 *   - `opsRef`: live snapshot of side-effect callbacks + props the reducer reads
 *
 * `opsRef.current` is refreshed in a `useLayoutEffect` — synchronous in the
 * commit phase, before browser paint and before any subsequent user input can
 * fire. So pointer handlers always see the freshest props, matching the
 * closure-fresh prop semantics of the pre-refactor handlers.
 *
 * Pointer capture (`set/releasePointerCapture`) lives in the hook, not the
 * reducer. It's per-event DOM glue keyed off transition shape:
 *   idle → non-idle  → capture
 *   non-idle → idle  → release
 *
 * @param deps All `StrokeOps` (the reducer's side-effect surface plus the live prop snapshot) plus `eventToCell`, the host's coord-translation callback. Identity may change every render — only the latest values are observed.
 * @returns Stable `onPointerDown/Move/Up/Cancel` handlers to wire onto the canvas wrapper. Identities never change across renders (zero deps on the inner `useCallback`s — all freshness flows through `opsRef`).
 */
export function usePointerTools(deps: PointerToolsDeps): PointerToolsHandlers {
  const stateRef = useRef<StrokeState>(IDLE);
  const opsRef = useRef<PointerToolsDeps>(deps);
  // Layout effect runs synchronously in the commit phase, before browser
  // paint and before any subsequent user input can fire. So pointer handlers
  // always see the freshest props — same effective semantic as the prior
  // closure-fresh handlers, satisfies `react-hooks/refs`.
  useLayoutEffect(() => {
    opsRef.current = deps;
  });

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    const ops = opsRef.current;
    const prevKind = stateRef.current.kind;
    stateRef.current = reduce(stateRef.current, {
      type: "down",
      cell: ops.eventToCell(e),
      clientX: e.clientX,
      clientY: e.clientY,
      button: e.button,
      shiftKey: e.shiftKey,
    }, ops);
    const nextKind = stateRef.current.kind;
    if (prevKind === "idle" && nextKind !== "idle") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (stateRef.current.kind === "idle") return;
    const ops = opsRef.current;
    stateRef.current = reduce(stateRef.current, {
      type: "move",
      cell: ops.eventToCell(e),
      clientX: e.clientX,
      clientY: e.clientY,
    }, ops);
  }, []);

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (stateRef.current.kind === "idle") return;
    const ops = opsRef.current;
    stateRef.current = reduce(stateRef.current, {
      type: "up",
      cell: ops.eventToCell(e),
    }, ops);
    if (stateRef.current.kind === "idle") safeReleaseCapture(e);
  }, []);

  const onPointerCancel = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (stateRef.current.kind === "idle") return;
    stateRef.current = reduce(stateRef.current, { type: "cancel" }, opsRef.current);
    if (stateRef.current.kind === "idle") safeReleaseCapture(e);
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
